/**
 * Canonical integration prompts and public-doc routes.
 *
 * Keep content here when it appears in both the dashboard and website docs so
 * an operator and an implementation agent receive the same instructions.
 *
 * Use {@link buildIntegrationPrompt} at copy/display time so the docs URL
 * matches the current environment and the working agreement is always attached.
 *
 * **Accuracy contract.** Every claim in a prompt is a claim about live
 * behaviour, and an agent has no way to tell a stale line from a current one.
 * When a public surface changes — a route, a required header, an error code, a
 * capability, a dashboard control an operator is told to use — update the guide
 * in the same change. The backing sources are:
 * `apps/panel-api/src/public-api/` (GraphQL schema + auth),
 * `apps/panel-api/src/subscription/controllers/` (REST + webhooks),
 * `apps/panel-api/src/subscription/guards/api-key-auth.guard.ts` (auth, scope,
 * rate limit) and `services/api-key/` (issue / rotate / capabilities).
 */

const DEFAULT_DOCS_ORIGIN = 'https://www.revvly.ai';

/** Production public API origin used in copy-paste integration prompts and examples. */
export const PUBLIC_API_ORIGIN = 'https://api.revvly.ai';

const WORKING_AGREEMENT = `Working agreement before you implement:
- Do not write production code until you have enough concrete context from the operator.
- Ask clarifying questions first — prefer one short round of questions over guessing.
- Confirm at least: source system / trigger, field mapping, where the API key and any signing secret will be stored, the target environment (local, staging, or production), and the Revvly tenantId when it is not already provided below.
- Once those answers are in place, implement the full integration against the contract below end-to-end.
- Open or fetch the Full guide URL if you need examples or edge-case detail beyond this prompt.
- Never invent API keys, signing secrets, region ids, subscription ids, or other tenant data.
- Never put secrets in browser code, logs, client bundles, or source control.`;

/**
 * Facts that hold on every API-key-authenticated Revvly surface, so no
 * individual guide has to restate them (and none can drift from the others).
 * Kept short — this is prepended to a contract the agent also has to read.
 */
const PLATFORM_LIMITS = `Platform rules that apply to every Revvly API call:
- Auth is a Bearer API key issued in the dashboard under Settings → API Keys. Tokens are prefixed \`rvly_sk_live_\`.
- Rate limits are per API key and default to 10 requests/second and 100 requests/minute. Overage is HTTP 429 with a \`Retry-After\` header in seconds (public GraphQL reports \`errors[].extensions.code = RATE_LIMITED\`). Honour Retry-After and back off — never retry a 429 in a tight loop.
- A key can be scoped to specific subscriptions and meters. List reads are filtered to that scope; reading or writing a single out-of-scope resource is 403 (\`FORBIDDEN\`), not an empty result.
- Every call is tenant-scoped by the key itself. There is no tenant parameter to switch — a different tenant means a different key.
- The token and the signing secret are server-side credentials: no browser code, no client bundles, no logs, no source control.`;

type GuideDefinition = {
  title: string;
  docsPath: string;
  /** One-line role framing for the agent. */
  role: string;
  /** Detailed contract the agent implements after it has enough context. */
  contract: string;
};

export const INTEGRATION_GUIDES = {
  prospectForm: {
    title: 'Send form prospects to Revvly',
    docsPath: '/docs/guides/prospect-form-integration',
    role: 'You are integrating an external form or webhook with Revvly CRM so each submission becomes a pipeline-ready prospect.',
    contract: `Goal
Turn one inbound form/webhook event into a Revvly prospect: organization (company) + contact (person) + lead (deal the sales team works).

Endpoint and auth
- POST ${PUBLIC_API_ORIGIN}/api/v1/graphql from a trusted backend or webhook worker only.
- Authorization: Bearer <API_KEY>
- Content-Type: application/json
- The API key needs WRITE capability ("Read & write" access level at issue time). CRM must be enabled for the tenant.
- Browser forms must POST to your backend; your backend forwards to Revvly. Never call Revvly from the browser with the API key.

Mutation
Use createProspect(input: CreateProspectInput!).
Do NOT use createClient — that creates a billing client, not a CRM prospect, and requires a region.

Required / optional fields
- organization.name — required (2–100 chars)
- organization.domain, email, description, industry, regionId — optional
- contact — include at least one of firstName, lastName, or email (enforced server-side; a contact with only a phone is rejected)
- lead.title — required (what the form is about, e.g. "Website demo request")
- lead.externalRef — required; set to the form provider's immutable submission or event id
- lead.estimatedValue, currency, and similar lead fields — optional when the form provides them
- Omit pipelineId and pipelineStageId unless the form must land in a specific pipeline; defaults are the tenant default pipeline and its first Open stage

Idempotency / retries
- Reuse the same lead.externalRef unchanged when the provider retries the same event.
- Do not generate a new externalRef on every delivery.
- A completed retry returns the previously created organization, contact, and lead instead of duplicating them.
- lead.externalRef is its own namespace. It is unrelated to a client's externalId or a subscription's externalId — do not reuse one as the other.

Validation and errors
- Organization domains and contact emails are unique per tenant; duplicates return errors[].extensions.code = CONFLICT.
- CONFLICT is also returned when a lead already exists for the externalRef but its organization or contact can no longer be resolved — that is a data-repair case, not something a retry fixes.
- FORBIDDEN means either CRM is not enabled for the tenant or the key lacks WRITE. UNAUTHENTICATED means the token is bad, revoked, or past its rotation grace window.
- Custom / arbitrary form fields are not a supported public API contract yet — send only documented organization, contact, and lead fields.
- Surface GraphQL failures through errors[].extensions.code; do not assume HTTP status alone.

Example mutation shape
mutation CreateProspect($input: CreateProspectInput!) {
  createProspect(input: $input) {
    organization { id name domain }
    contact { id firstName lastName email }
    lead { id title externalRef pipelineId pipelineStageId }
  }
}

Example variables
{
  "input": {
    "organization": {
      "name": "Northstar Labs",
      "domain": "northstarlabs.example",
      "industry": "Software"
    },
    "contact": {
      "firstName": "Avery",
      "lastName": "Chen",
      "email": "avery@northstarlabs.example"
    },
    "lead": {
      "title": "Enterprise demo request",
      "externalRef": "form-submission-evt_01J8K3",
      "estimatedValue": 24000,
      "currency": "USD"
    }
  }
}

Implementation checklist
1. Verify webhook authenticity on your side before forwarding.
2. Map provider fields → organization / contact / lead.
3. Persist or log the Revvly ids returned for support, but keep using the provider submission id as externalRef for retries.
4. Add tests for happy path, retry with the same externalRef, and CONFLICT on duplicate domain/email when that is a real risk.`,
  },
  clientApi: {
    title: 'Integrate with public GraphQL',
    docsPath: '/docs/guides/public-graphql',
    role: 'You are integrating a trusted backend with Revvly public GraphQL to provision billing clients and subscriptions, and read receivables.',
    contract: `Goal
Give a backend one intentional contract for billing-client and subscription creation, and receivables reads (invoices, payment summary, PDF download links).

Endpoint and auth
- POST ${PUBLIC_API_ORIGIN}/api/v1/graphql
- Authorization: Bearer <API_KEY>
- Content-Type: application/json
- Mutations require WRITE capability on the API key ("Read & write" access level at issue time); queries need only READ.
- Introspection is enabled on this endpoint — the curated schema is the contract, so introspect it rather than guessing field names.
- Query nesting is capped at depth 8; deeper documents are rejected before execution with QUERY_DEPTH_EXCEEDED.
- Never expose the API key in browser code.

Important distinction
- createClient provisions a billing Client and requires a valid active region.
- Inbound lead / demo forms must use createProspect instead (CRM), not createClient.

Read a client
- client(id: ...) or client(externalId: ...) — supply exactly one; supplying both or neither is BAD_REQUEST.
- Nested fields of interest: region, subscriptions, invoices(status, limit), paymentSummary, and invoice.pdfUrl
- invoices returns newest first; limit defaults to 100 and caps at 1000. status filters to one payment status (PAID | PENDING | OVERDUE | FAILED | CANCELLED). There is no cursor pagination on invoices yet.
- paymentSummary is one entry per currency the client has been invoiced in: currency, outstanding (PENDING + OVERDUE + FAILED), overdue, invoiceCount, overdueCount
- pdfUrl is a short-lived (~30 min) presigned URL, minted fresh per query and null when no PDF exists — request it only when the caller will download immediately
- If the API key is scoped to specific subscriptions, subscriptions / invoices / paymentSummary are all filtered to that scope, so totals will not match the dashboard's. Use an unscoped key for receivables reporting.

Provision a billing client
1. Discover an active region with GET ${PUBLIC_API_ORIGIN}/api/v1/regions
2. Call createClient with at least name and regionId
3. Store your own externalId at creation time so later reads can use client(externalId: ...) without storing Revvly ids if you prefer

Example create
mutation CreateClient($input: CreateClientInput!) {
  createClient(input: $input) {
    id
    externalId
    name
    region { id name defaultCurrency }
  }
}

{
  "input": {
    "name": "Acme Corporation",
    "regionId": "<active-region-id>",
    "externalId": "acct_123",
    "paymentTermsDays": 30
  }
}

Provision a subscription for that client
- Call createSubscription with clientId or clientExternalId (exactly one), period, startDate, and at least one of bundleId / moduleId / moduleIds / variableComponentIds / components — a subscription that bills nothing is BAD_REQUEST.
- Set externalId on the subscription itself, separate from the client's externalId. This is the id the usage/event APIs resolve against as externalSubscriptionId — without it, a subscription created here has nothing for a usage-reporting integration to find, and every usage call for it 404s until someone sets it by hand on the dashboard.
- Use components[] to seed a starting quantity per Variable Component at creation (e.g. an initial seat/child count) via startingQuantity, and an inclusion via includedQuantity. This seeds the initial usage counter so the start invoice bills correctly and later usage reports are deltas from that baseline, not raw totals.
- Deliberately not exposed here: historical invoice backfill, billing schedules, initial discounts, legacy catalog-version pins, and plan-change timelines. Those stay dashboard-only (the subscription wizard's createSubscriptionWithPricing on the internal, JWT-authed GraphQL API); this mutation covers provisioning a new subscription going forward.
- A duplicate externalId (client or subscription) is CONFLICT.

Example create
mutation CreateSubscription($input: CreateSubscriptionInput!) {
  createSubscription(input: $input) {
    id
    externalId
    status
    period
    currency
    startDate
    endDate
    autoRenew
  }
}

{
  "input": {
    "clientExternalId": "acct_123",
    "externalId": "sub-acme-growth-2026",
    "bundleId": "<bundle-id>",
    "period": "MONTHLY",
    "startDate": "2026-08-01T00:00:00.000Z",
    "components": [
      { "componentId": "<component-id>", "startingQuantity": 2 }
    ]
  }
}

Example read
query ClientBillingStatus($externalId: String!) {
  client(externalId: $externalId) {
    id
    name
    invoices(status: "OVERDUE", limit: 10) {
      invoiceNumber
      dueDate
      amount { amount currency }
      paymentStatus
      pdfUrl
    }
    paymentSummary { currency outstanding overdue overdueCount }
  }
}

External id namespaces
- A client's externalId and a subscription's externalId are separate fields on separate objects. The usage and event APIs resolve externalSubscriptionId against the SUBSCRIPTION's externalId — passing a client externalId there returns 404.

Errors
- Handle GraphQL errors through errors[].extensions.code. The stable codes are UNAUTHENTICATED (401), FORBIDDEN (403 — missing WRITE, or out-of-key-scope), RATE_LIMITED (429), BAD_REQUEST (400), NOT_FOUND (404), CONFLICT (409, e.g. duplicate client name or externalId), QUERY_DEPTH_EXCEEDED, and INTERNAL_ERROR (server faults are masked).
- Do not treat a non-2xx HTTP status alone as the only signal; inspect the GraphQL body.
- Retry only UNAUTHENTICATED-free transport faults and RATE_LIMITED (after Retry-After). CONFLICT / BAD_REQUEST / NOT_FOUND will not change on retry.

Implementation checklist
1. Confirm whether the operator needs create, read, or both — and, for create, whether it is a client, a subscription, or both.
2. Confirm how they will choose regionId (fixed region vs lookup).
3. Confirm the externalId scheme they already use in their system of record — a client and its subscription each need their own.
4. For a subscription, confirm the bundle/module/component selection and any starting quantities (components[].startingQuantity) with the operator; do not invent catalog ids.
5. Implement server-side only, with retries that are safe for the operations you call.`,
  },
  usageReporting: {
    title: 'Report usage through REST',
    docsPath: '/docs/guides/usage-reporting',
    role: 'You are implementing Revvly REST usage reporting from a trusted backend that owns deltas, absolute counts, or commission sales.',
    contract: `Goal
Report metered usage into Revvly so variable components can bill against the correct subscription and meter.

Base URL and auth
- Host: ${PUBLIC_API_ORIGIN}
- Authorization: Bearer <API_KEY> on every request
- Every mutating usage call MUST send an Idempotency-Key header
- Usage endpoints do NOT use the HMAC signing header required by POST ${PUBLIC_API_ORIGIN}/api/v1/events
- Never expose the API key in browser code

Choose the right endpoint
- POST ${PUBLIC_API_ORIGIN}/api/v1/usage — body \`delta\`: a positive or negative integer applied to the current count
- POST ${PUBLIC_API_ORIGIN}/api/v1/usage/set — body \`quantity\`: replaces the current quantity with an absolute count
- POST ${PUBLIC_API_ORIGIN}/api/v1/usage/commission — body \`saleAmount\` + \`transactionId\` for commission billing
- POST ${PUBLIC_API_ORIGIN}/api/v1/usage/batch — body \`items[]\`, up to 100 independent deltas; response is HTTP 207 with per-item results
- All four also accept optional \`reportedAt\` (ISO 8601) and \`notes\`; each returns the resulting currentCount, peakCount, cumulativeCount and peakReachedAt.
- Prefer POST ${PUBLIC_API_ORIGIN}/api/v1/events instead when the source emits a stream of individually-identified events you want deduplicated on an event id. Never send the same consumption through both surfaces.

Identity rules (every item)
- Exactly one subscription reference: subscriptionId OR externalSubscriptionId
- Exactly one meter reference: variableId OR variableSlug
- Prefer externalSubscriptionId + variableSlug when the operator already has those external references
- externalSubscriptionId resolves against the SUBSCRIPTION's externalId — not the client's externalId, and not any CRM externalRef. Confirm the value is set on the subscription before building on it.
- Supplying both or neither of a pair is 400; a reference that does not resolve is 404; a reference that resolves outside the key's scope is 403.

Discover references
- GET ${PUBLIC_API_ORIGIN}/api/v1/variables — meters with their id and slug (add ?includeInactive=true to see archived ones)
- GET ${PUBLIC_API_ORIGIN}/api/v1/subscriptions — keyset-paginated summaries; filter with ?organizationId= / ?externalId= / ?status= and follow nextCursor
- GET ${PUBLIC_API_ORIGIN}/api/v1/subscriptions/by-external/:externalId — resolve one subscription by your own id
- These reads are Bearer-only and filtered to the key's scope.

Read back what you reported
- GET ${PUBLIC_API_ORIGIN}/api/v1/usage/:subscriptionId/:variableId — current state (currentCount, peakCount, cumulativeCount, peakReachedAt)
- GET ${PUBLIC_API_ORIGIN}/api/v1/usage/:subscriptionId/:variableId/history — the audit log, with ?since= / ?until= / ?limit= (default 100, max 1000) and a hasMore flag; every entry carries the idempotencyKey that produced it
- Use these to reconcile after a failed run instead of re-posting and hoping.

Idempotency
- Reuse the same Idempotency-Key only when retrying the same logical usage event
- For /api/v1/usage/batch, also give every item a unique idempotencyKeySuffix (the effective key is \`<header>::<suffix>\`)
- Inspect each results[].status independently on HTTP 207 — do not treat 207 as blanket success. Per-item statuses are numeric HTTP-style codes (200, 400, 403, 404, 422).
- A replay of an applied key returns the original outcome and applies nothing.

Example delta
POST ${PUBLIC_API_ORIGIN}/api/v1/usage
Authorization: Bearer <API_KEY>
Idempotency-Key: usage-event-evt_01J8K3
Content-Type: application/json

{
  "externalSubscriptionId": "sub-acme-growth-2026",
  "variableSlug": "api-calls",
  "delta": 1500,
  "reportedAt": "2026-08-02T14:00:00.000Z",
  "notes": "Hourly usage rollup"
}

Example batch
POST ${PUBLIC_API_ORIGIN}/api/v1/usage/batch
Authorization: Bearer <API_KEY>
Idempotency-Key: hourly-rollup-2026-08-02T14

{
  "items": [
    {
      "externalSubscriptionId": "sub-acme-growth-2026",
      "variableSlug": "api-calls",
      "delta": 1500,
      "idempotencyKeySuffix": "sub-acme-growth-2026"
    },
    {
      "externalSubscriptionId": "sub-northwind-scale-2026",
      "variableSlug": "api-calls",
      "delta": 900,
      "idempotencyKeySuffix": "sub-northwind-scale-2026"
    }
  ]
}

Implementation checklist
1. Confirm which mutation fits the source system (delta vs set vs commission vs batch).
2. Confirm the external subscription id and meter slug/id mapping, and that the subscription actually carries that externalId.
3. Confirm idempotency key strategy (event id, period key, etc.).
4. Store/log per-item batch failures for retry without double-counting successful items.
5. Do not use ${PUBLIC_API_ORIGIN}/api/v1/events HMAC signing for these usage routes.`,
  },
  eventIngestion: {
    title: 'Stream signed usage events',
    docsPath: '/docs/api/events',
    role: 'You are implementing signed Revvly event ingestion from a trusted backend that streams individually-identified metered usage or commission events.',
    contract: `Goal
Stream immutable, deduplicated usage events into Revvly so meters aggregate them without your side tracking running totals.

Endpoint and auth
- POST ${PUBLIC_API_ORIGIN}/api/v1/events — Bearer token AND an HMAC signature. Both are required.
- Authorization: Bearer <API_KEY>
- X-Signature-SHA256: sha256=<hex HMAC-SHA256 of the exact raw request body under the key's signing secret>. Bare hex without the prefix is also accepted.
- Content-Type: application/json
- Serialize the JSON once, HMAC those exact bytes, send those same bytes. Re-serializing after signing (or letting an HTTP client re-encode the body) invalidates the signature.
- The signing secret is a second credential, shown once alongside the token at issue and rotation. A key issued before signing secrets existed has none and this endpoint answers 401 "API key has no signing secret" until the key is rotated.
- There is no Idempotency-Key header here — the per-event eventId is the idempotency key.

Batch shape
{ "events": [ … ] } with 1 to 1000 events. An empty or non-array \`events\` is 400; more than 1000 is 400.

Per-event fields
- eventId — required. Stable and unique per tenant; ULID recommended. This is the dedup key: a replay returns DUPLICATE and applies nothing.
- Exactly one subscription reference: subscriptionId OR externalSubscriptionId (the SUBSCRIPTION's externalId, not the client's)
- Exactly one meter reference: variableId OR variableSlug
- type — USAGE_REPORTED (the default when omitted), USAGE_CORRECTED, or COMMISSION_EVENT
- quantity — required for USAGE_REPORTED / USAGE_CORRECTED
- saleAmount + transactionId — required for COMMISSION_EVENT
- reportedAt — required ISO 8601. No more than 15 minutes in the future and no more than 7 days in the past, or the event is REJECTED for clock skew. Backfills older than 7 days need an operator, not a retry.
- context — optional free-form object carried into the audit trail

Reading the response
HTTP 202 means the batch was accepted for processing — it is NOT a per-event success. Inspect every results[] entry; they come back in the order you sent them.
- ACCEPTED — validated and queued; this is the normal success status
- PROCESSED — applied synchronously (returned when the queue is bypassed)
- DUPLICATE — this eventId was already seen; the original outcome stands. Safe, and not an error.
- REJECTED — rejectionReason carries the cause (unresolved reference, missing field, clock skew, scope violation). Surface it; replaying the same payload will not fix it.
One bad event never sinks the batch — the rest still apply.

Discover references
- GET ${PUBLIC_API_ORIGIN}/api/v1/variables — meter ids and slugs
- GET ${PUBLIC_API_ORIGIN}/api/v1/subscriptions and /subscriptions/by-external/:externalId — subscription references
- Discovery reads are Bearer-only; no HMAC on reads.

Choosing this endpoint over /api/v1/usage
- /api/v1/events when the source emits discrete events with their own ids, and you want dedup on eventId plus an append-only trail.
- /api/v1/usage* when you hold the delta or the absolute count and want a synchronous answer with the resulting totals.
- Never send the same consumption through both.

Example
POST ${PUBLIC_API_ORIGIN}/api/v1/events
Authorization: Bearer <API_KEY>
X-Signature-SHA256: sha256=<hmac-hex>
Content-Type: application/json

{
  "events": [
    {
      "eventId": "01HYZ7K2N8V3WXPQ4JCBM5FGTE",
      "externalSubscriptionId": "sub-acme-growth-2026",
      "variableSlug": "api-calls",
      "type": "USAGE_REPORTED",
      "quantity": 1500,
      "reportedAt": "2026-08-02T14:00:00.000Z"
    }
  ]
}

Implementation checklist
1. Confirm where eventId comes from (a provider event id, or a ULID you mint and persist before sending).
2. Confirm where the signing secret lives and that it rotates together with the Bearer token.
3. Sign raw bytes — assert in a test that the buffer you signed is the buffer you wrote to the socket.
4. Persist per-event outcomes so REJECTED rows can be repaired without replaying ACCEPTED ones.
5. Add tests for happy path, duplicate eventId, a clock-skew rejection, and one bad reference inside an otherwise-valid batch.`,
  },
  invoicePayments: {
    title: 'Post payments to invoices',
    docsPath: '/docs/guides/webhook-payment',
    role: 'You are forwarding confirmed payments from a PSP, bank feed, or accounting system into Revvly so invoices settle without dashboard work.',
    contract: `Goal
Record a full or partial payment against a Revvly invoice as soon as your payment source confirms it, idempotently.

Endpoint and auth
- POST ${PUBLIC_API_ORIGIN}/api/v1/webhooks/invoice-payment
- Authorization: Bearer <API_KEY>
- X-Signature-SHA256: sha256=<hex HMAC-SHA256 of the exact raw JSON body under the key's signing secret>
- Idempotency-Key: required, stable per payment — use the provider's payment or event id
- Content-Type: application/json
- Serialize once, HMAC those bytes, POST those bytes. A key with no signing secret answers 401 until it is rotated.
- This marks money as received. Only post payments your source has confirmed as settled.

Body
- invoiceId OR invoiceNumber — at least one is required; invoiceId wins when both are sent. invoiceNumber is usually the handle a PSP or bank reference carries, formatted {PREFIX}-{SEQUENCE} (e.g. ACME-00042).
- amount — omit to settle the whole outstanding balance; send a positive amount for a part payment. A number or a decimal string. It must be strictly greater than zero and must still be greater than zero after rounding to the currency's minor unit — do NOT send 0 to mean "settle in full" (that is a 400; omit the field instead), and do not send a sub-cent amount like 0.004.
- currency — optional. When sent it must equal the invoice currency, or the call is 400.
- paidAt — optional ISO 8601; defaults to now.
- reference, notes — optional free text for the audit trail.
- tenantId — optional guard only. The tenant always comes from the API key; a mismatch is rejected 403 as a misdirected webhook, never a tenant switch.

Response and idempotency
- 200 with invoiceId, invoiceNumber, paymentStatus, currency, amount, amountPaid, amountDue, fullyPaid, appliedAmount, actualPaymentDate.
- A repeat of the same Idempotency-Key returns 200 with replayed: true and appliedAmount: 0. Retries are safe and cost nothing.
- Partial payments accumulate: the invoice keeps its current status (PENDING, OVERDUE, or FAILED — a failed collection attempt can still take a payment) until amountPaid covers the total, then transitions to PAID through the normal state machine. Only full settlement moves the status.

Errors
- 409 — the invoice is a held draft, already PAID or CANCELLED, or has no outstanding balance. Do NOT retry; route it to reconciliation.
- 404 — no invoice matched that id or number for this tenant.
- 400 — neither identifier supplied, malformed invoiceId, unparseable paidAt, a currency mismatch, or an amount that is unparseable, non-finite, zero/negative, or rounds to zero.
- 401 — bad token, or a missing/invalid signature. 403 — the endpoint is outside the key's scope, or a supplied tenantId is not the key's tenant.

Implementation checklist
1. Confirm which invoice handle the payment source carries (invoiceNumber is the usual one) and how it gets onto the payment.
2. Confirm the provider event id used as the Idempotency-Key, and that it is stable across redeliveries.
3. Decide the reconciliation path for 409 and 404 — queue for a human, never drop silently.
4. Keep the raw body byte-identical between signing and sending, including on retries.
5. Add tests for full settlement, part payment then settlement, redelivery replay, and a 409 on an already-paid invoice.`,
  },
  apiKeys: {
    title: 'Configure integration API keys',
    docsPath: '/docs/guides/api-keys',
    role: 'You are helping an operator configure a Revvly integration API key with the least privilege their worker needs.',
    contract: `Goal
Issue or rotate credentials correctly, store them once in a server-side secret manager, and choose the right auth model for each Revvly endpoint family.

Where keys are managed
- Dashboard: Settings → API Keys
- The issue dialog sets: a name, an Access level (Read, or Read & write), and optional subscription / meter scope.
- On issue and on rotation, the Bearer token and the signing secret plaintext are shown only once — store them immediately.
- Never return either secret to browsers, logs, CI output, or source control.

Auth models by endpoint family
- Public GraphQL (POST ${PUBLIC_API_ORIGIN}/api/v1/graphql): Bearer token. Mutations such as createClient, createSubscription, and createProspect need WRITE.
- Usage REST (${PUBLIC_API_ORIGIN}/api/v1/usage*): Bearer token + Idempotency-Key on mutations. No request HMAC.
- Discovery REST (GET ${PUBLIC_API_ORIGIN}/api/v1/subscriptions, /variables, /regions, and the usage GETs): Bearer token only.
- Event ingestion (POST ${PUBLIC_API_ORIGIN}/api/v1/events): Bearer token AND HMAC-SHA256 of the raw body with the signing secret. The eventId is the idempotency key; there is no Idempotency-Key header.
- Invoice payment webhook (POST ${PUBLIC_API_ORIGIN}/api/v1/webhooks/invoice-payment): Bearer token, raw-body HMAC with the signing secret, and a stable Idempotency-Key.

Capabilities and scope (current product behavior)
- Capabilities are deliberately coarse: READ is always present, WRITE is the opt-in that public GraphQL mutations require. Choose "Read & write" at issue time when the worker mutates; there are no per-resource capabilities.
- WRITE is enforced on any mutation at the endpoint level, so a read-only key fails every mutation with FORBIDDEN regardless of which one it calls.
- Subscription and meter (variable) scope can be narrowed at issue time; leave both empty for tenant-wide access. Scope filters list reads and blocks single-resource reads/writes outside it — including the GraphQL client's subscriptions / invoices / paymentSummary, so a scoped key reports partial receivables.
- Endpoint scope exists on the key but is NOT exposed in the dashboard issue dialog: a key issued there is unrestricted by endpoint. Narrowing it means issuing through the internal issueApiKey mutation with scope.endpoints (paths with the /api/v1 prefix stripped, e.g. ["/usage", "/usage/*"]).
- Rate limits default to 10 requests/second and 100/minute per key (support secret keys: 50/second and 3,000/minute). A tenant admin can change them, up to 500/second and 30,000/minute, through the internal updateApiKeyRateLimits mutation or, for support secret keys, Settings → Support → Secret keys → Limits. Plan for 429 + Retry-After in the worker regardless.

Rotation
1. Rotate in the dashboard. This mints a NEW token and a NEW signing secret, and the old pair keeps working for a 30-day grace window.
2. Store both new values, then deploy them to the worker.
3. Retire the old values once the worker is fully on the new pair — you do not have to swap atomically, the grace window is the overlap.
4. Revocation is immediate and ignores the grace window — revoke only after the worker is off the key.
5. A key issued before signing secrets existed has none; the HMAC endpoints answer 401 "API key has no signing secret. Rotate to provision one." Rotation is the fix.

Implementation checklist
1. Confirm which endpoint family the worker calls (GraphQL, usage, discovery, events, payment webhook, or a mix).
2. Confirm whether WRITE is required — if it is, the key must be issued as "Read & write".
3. Confirm whether subscription/meter scope should be narrowed, and that narrowing will not starve a reporting read the same key performs.
4. Confirm the secret manager and rotation runbook the operator uses.
5. Document which secret is used for Bearer vs HMAC so they are not swapped.`,
  },
  productEvents: {
    title: 'Send product events to Revvly',
    docsPath: '/docs/guides/product-events-setup',
    role: "You are adding product usage tracking to the operator's product so Revvly shows how each of the operator's clients uses it: events sent from the operator's backend and, optionally, page views and time on page sent from the browser with a short-lived token.",
    contract: `Goal
Report what the operator's users do (a report exported, a feature opened, a page viewed) so Revvly shows it on each client's Usage tab, in the cross-client overview and in the support ticket of the person who wrote in. Nothing sent this way is billed; for billable metering use the Event Ingestion API instead.

Credentials, never mixed
- API key (rvly_sk_live_…): created in the Revvly dashboard under Settings → API Keys with "Can send product events" ticked. That tick only appears once Revvly has switched Product usage on for the workspace. Server-side only: it never goes in a page, an app bundle, logs, URLs or git.
- Browser token: a short-lived (60 s to 1 h, default 15 min) token the operator's backend asks for at POST ${PUBLIC_API_ORIGIN}/api/v1/product-events/tokens, one per signed-in person. The page sends with it to POST ${PUBLIC_API_ORIGIN}/api/v1/product-events/browser. Revoking the key stops every token it minted.

Step 1 — Operator setup in the Revvly dashboard (ask the operator to confirm each is done; you cannot do it for them)
- Product usage is switched on for the workspace (Settings → Product events appears, and the key form shows "Can send product events").
- An API key with that tick exists and is stored in the backend's secret store, e.g. REVVLY_PRODUCT_EVENTS_KEY.
- Every client has an External ID (Edit client → External ID) equal to the clientId the product will send. An event whose clientId matches no client is stored but shown on no client.
- For browser tracking: every site that will send is listed under Settings → Product events → Browser tracking → Allowed sites, one per line, e.g. https://app.example.com. https://*.example.com covers every subdomain but not example.com itself, another scheme or another port; each white-label domain is its own line; at most 100 lines.

Step 2 — Send events from the backend
- POST ${PUBLIC_API_ORIGIN}/api/v1/product-events with Authorization: Bearer <API_KEY>, body { "events": [ ... ] }, 1 to 100 events, answered 202 with one result per event (ACCEPTED, DUPLICATE or REJECTED with a reason). One bad event never sinks the others.
- Each event: event (required, up to 128 characters, a stable name like report.exported), userId and/or clientId (at least one, your own ids, up to 255 characters), eventId (optional but send it: the same id again returns DUPLICATE, so retries are safe), timestamp (optional ISO 8601, up to 15 minutes ahead and 30 days back), properties (optional flat object, up to 50 keys; strings up to 500 characters, numbers, booleans or null; no nesting).
- Send ids, not personal data. A property named like a credential, and any value that is an email, a JWT or a card number, is stored as [redacted].

Step 3 — Browser tracking (optional)
- Add one backend route that reads the signed-in person from the operator's own session (never from the request) and calls POST /api/v1/product-events/tokens with { userId, clientId, ttlSeconds }. Return the token string.
- Load the script from the operator's Revvly address, <script src="https://<operator's Revvly address>/revvly-events.js"></script>, then RevvlyEvents.init({ getToken: () => fetch('/their-route').then(r => r.text()) }). There is no package to install or upgrade: the script is served by Revvly and a page gets the current version on each load.
- It records page.viewed and page.left (with active seconds) for you; RevvlyEvents.track('name', { ...flat properties }) adds your own. Ids in paths become :id; the query string, hash and page title are not sent. Offer respectDoNotTrack: true and RevvlyEvents.optOut() / optIn().

Step 4 — Verify
- Settings → Product events lists each new event name under New; an admin names and organises them.
- The client's Usage tab fills in. A 403 on the browser route means the page's origin is not under Allowed sites; a 401 means the token expired or its key was revoked.

Clarifying questions to ask the operator first
1. Which clients' ids the product uses, and whether each is already an External ID on the client record.
2. How the backend identifies the signed-in person and company (session, JWT) for the token route.
3. Whether to send only from the backend or also from the browser, and every origin (production, staging, local) that will send.
4. Which events matter (the features worth knowing a client uses), so names stay stable.
5. Whether the operator's own privacy notice already covers product usage measurement.`,
  },
  supportWidget: {
    title: 'Add a support chat to your app',
    docsPath: '/docs/guides/support-widget',
    role: "You are adding a Revvly Support chat to the operator's product so their signed-in users can open support requests and chat with the operator's support team, using a backend token route and a widget built on the Revvly support chat API.",
    contract: `Goal
Let a signed-in user of the operator's product open a support request, see their requests, chat with the support team and get replies, from inside the product. Revvly Support stores the conversations and the operator's agents answer them in the Revvly Support inbox.

Two credentials, never mixed
- Support secret key (rvly_sk_live_…): created in the Revvly dashboard under Settings → Support → Secret keys (NOT Settings → API Keys). Server-side only. Its only job here is minting end-user tokens. It can read tenant data but cannot change anything.
- End-user token: a short-lived (≤ 1 h) RS256 JWT for one user, minted by the backend and handed to the browser or app. Every chat call uses it. It is not accepted on ${PUBLIC_API_ORIGIN}/api/v1/graphql, and the secret key is not accepted on the chat endpoint.

Step 1 — Operator setup in the Revvly dashboard (ask the operator to confirm each is done; you cannot do it for them)
- Revvly Support is enabled for the workspace (Support appears in the sidebar).
- A support secret key exists (Settings → Support → Secret keys → Create secret key). The plaintext is shown once; it goes into the backend's secret store as e.g. REVVLY_SUPPORT_SECRET_KEY.
- Every origin the chat runs on is listed under Settings → Support → General → Widget allowed origins (https only, origin only; https://*.example.com covers every subdomain but not example.com itself, so list the bare domain and each white-label domain on their own lines; up to 100; http://localhost is allowed for development). A browser call from any other origin is refused with FORBIDDEN. Changes take up to a minute.
- If the product passes support roles, each role is declared under Settings → Support → Roles first; an undeclared role is refused at mint time with BAD_REQUEST.

Step 2 — Backend: a token route
- Add one authenticated route (e.g. POST /api/support-token) that only works for a user the product's own session has signed in. Never mint for an unauthenticated caller or for a user id taken from the request body.
- It calls POST ${PUBLIC_API_ORIGIN}/api/v1/graphql with Authorization: Bearer <support secret key>:
  mutation CreateEndUserToken($input: EndUserClaimsInput!) {
    createEndUserToken(input: $input) { token expiresAt }
  }
- Claims (input):
  - externalUserId — required. The product's own stable user id (max 200). Never reuse one for a different person.
  - name — optional, shown to agents (max 200).
  - email — optional. Needed if the user should get an email about replies they have not read in the app.
  - role — optional, a declared support role (case-insensitive).
  - plan — optional (max 64).
  - organizationExternalId — optional, the user's account by the externalId set on the client in Revvly; links new requests to that client, and the user to that client's contact with the same email.
  - attributes — optional, up to 25 { key, value } facts agents see beside the user (e.g. company_stage, cohort). Key: a letter, then up to 39 letters, digits, _, - or .; value up to 500 chars. The list replaces the stored one; omit it to keep what is stored, [] clears it. Stored by Revvly, not put in the token.
  - ttlSeconds — optional, 60–3600, default 3600.
- Cache the token per externalUserId in the backend's memory or its existing cache (e.g. Redis) and return the cached one until about a minute before expiresAt; mint a new one only then. Every page load and tab otherwise mints its own token, and a login rush then spends the key's rate limit on tokens that are still valid. Key the cache on the same claims you send, so a change of name, email, role, plan or organization mints a fresh token. Never write tokens to a database.
- Return { token, expiresAt } to the caller. Do not log the token and do not put it in a URL.
- The secret key's limits apply to all of the product's users together: 50 mints/second and 3,000/minute by default. With tokens cached for their hour that is enough for well over 100,000 sign-ins an hour; if it is not, the operator raises them under Settings → Support → Secret keys → Limits, which also shows the last 24 hours of traffic and how much was refused.
- Mint errors (errors[].extensions.code): UNAUTHENTICATED (secret key missing, wrong, revoked or past rotation grace), FORBIDDEN (not a support secret key, or Support not enabled), BAD_REQUEST (invalid claim, undeclared role, bad ttlSeconds), RATE_LIMITED (honour Retry-After).

Optional — Sync users in bulk
- To have users in Support → End users before they first open the chat, or to update attributes between sign-ins, call upsertSupportUsers(users: [SupportUserInput!]!) on the same endpoint with the same secret key: the same fields as the claims above without ttlSeconds, up to 500 users per call, matched by externalUserId. It never mints a token.
- It answers { created updated failed results { index externalUserId status code message organizationLinked contactLinked } }. Each user succeeds or fails on its own (FAILED with BAD_REQUEST for an invalid field or undeclared role, CONFLICT for an anonymised user); log the failures, do not retry them unchanged. organizationLinked: false means no client in Revvly has that externalId yet.
- Run it from a job (e.g. nightly, or after a user's profile changes), never from a browser.

Step 3 — Frontend: the chat client
- Endpoint: POST ${PUBLIC_API_ORIGIN}/api/v1/support/graphql, Content-Type: application/json, Authorization: Bearer <end-user token>. Standard GraphQL { query, variables } body. No cookies: never send credentials.
- Keep the token in memory. Fetch one from the backend route when the chat opens; fetch a new one a minute before expiresAt, and on any UNAUTHENTICATED answer (then retry that call once).
- Introspection works with any valid token; the operations below are the whole contract.

Step 4 — Frontend: the chat UI, using exactly these operations
- query { me { externalUserId name email role unreadCount unreadTickets } myNotificationPreferences { progressUpdates emailWhenOffline } ticketTypes { id key name } } — ticketTypes are the kinds of request to offer, in display order. me.unreadCount is the total of unread replies across all the user's requests (unreadTickets: how many requests have one); use it for the launcher badge.
- myTickets(statusCategory, first, after) → { nodes { id number title type { name } status { key name category } createdAt lastMessageAt closedAt unreadCount progress { key label reachedAt } } pageInfo { hasNextPage endCursor } } — the user's requests, most recent activity first. first is 1–100 (default 20).
- ticket(id) → Ticket or null (null for any id that is not the user's own).
- ticketMessages(ticketId, first, before) → { nodes { id author body createdAt } pageInfo { hasPreviousPage startCursor } } — the latest page, oldest first within it; pass startCursor as before for older messages and prepend them. first is 1–100 (default 50).
- createTicket(input: { typeId, body, title, clientMessageId, attachmentIds, source }) → { ticket { … } message { … } } — opens a request with its first message. body max 20,000 chars, title optional max 200. source optional: where in the product it was opened (e.g. "web", "ios", "billing-page"; a letter or digit then up to 63 of A–Z a–z 0–9 _ - . / :) — agents can filter by it, so pass a stable value per surface.
- sendMessage(input: { ticketId, body, clientMessageId }) → Message — a reply; a reply to a closed request reopens it.
- markRead(ticketId) → Ticket — call when the user actually sees the conversation; sets unreadCount to 0 and cancels the email about that unread reply.
- updateMyNotificationPreferences(input: { emailWhenOffline: Boolean, progressUpdates: ALL | FINAL | NONE }) → { progressUpdates emailWhenOffline } — the user's own choices (myNotificationPreferences reads them). The reply email is on by default and every such email carries a link to turn it off; offer a toggle in the chat's settings. Only the user can change them: minting a token never turns it back on. progressUpdates controls the stage notices the user receives as the work behind a request moves forward.
- Attachments (optional): POST ${PUBLIC_API_ORIGIN}/api/v1/support/chat/attachments?filename=<name>[&ticketId=<id>] with Authorization: Bearer <end-user token>, Content-Type: the file's type, and the raw file as the body (fetch with body: file). Returns { attachment: { id filename mimeType size url previewUrl } }; errors as { error: { code, message } }. Omit ticketId when the file is for a request not yet created. Then pass attachmentIds: [id, …] (max 10) on sendMessage for that ticket, or on createTicket. Allowed: PNG, JPEG, GIF, WebP, PDF, plain text; max 20 MB (413 above). Message.attachments lists files; show previewUrl inline for images and url as a download link; both expire in 30 minutes, so re-read rather than store them. Limits: 2 uploads/second, 20/minute per user.
- closeTicket(input: { ticketId, reason, comment }) → Ticket — reason is SOLVED | NO_LONGER_NEEDED | OTHER; comment optional (max 2,000). Closing a closed request returns it unchanged.
- clientMessageId: generate a fresh unique id per send (crypto.randomUUID(), max 100 chars). Retrying with the same id returns the original request or message instead of posting twice, so retry with the same id after a timeout or network error.
- message.author is YOU (the user), AGENT (the support team, never named), AI or SYSTEM. Bodies are plain text: render as text, never as HTML.
- status.name is the operator's own status name; branch on status.category (OPEN | IN_PROGRESS | PENDING | CLOSED). PENDING means waiting on the user and closes by itself after the operator's quiet period unless the user replies.

Step 5 — Live updates (server-sent events)
- Stream: GET ${PUBLIC_API_ORIGIN}/api/v1/support/chat/stream, one per signed-in user while the chat is open; close it when the chat closes.
- Browser: EventSource cannot send Authorization, so call the chat mutation createStreamTicket { path expiresAt } and open new EventSource(API origin + path). The ticket lasts one minute, works only on the stream, and must be fetched fresh for every connection. Apps and servers may instead send Authorization: Bearer <end-user token> with any SSE client.
- Frames: ready { resumed } ends the handshake (resumed false → re-read the list and the open thread); ticket_updated { ticketId, change: created|message|status|type|title|closed|reopened|progress, messageId, by: you|agent|ai|system, eventId } → re-read that request with the queries above (the frame carries no content; dedupe on eventId); typing { ticketId, expiresInMs } → show "Support is typing" on that request for expiresInMs; reauthenticate → close and reconnect with a new ticket or token.
- Resume: keep the last frame id (event.lastEventId) and pass it as ?lastEventId= on the next connection (or the Last-Event-ID header); missed frames arrive before ready.
- On error: close the EventSource (the ticket is spent) and reconnect after a few seconds with a new ticket. A 503 means live updates are unavailable: fall back to re-reading ticketMessages every 3–5 s and myTickets every 10–30 s while the chat is open and the tab visible.
- Use me.unreadCount for the launcher badge and each ticket's unreadCount in the list, and call markRead when the user views a conversation with unreadCount > 0.
- The stream ends by itself at the token's expiry and after 15 minutes at most; that is normal, reconnect.

Optional — Live updates through the operator's backend (a proxy)
- Only when the user's browser talks to the operator's backend rather than to Revvly. Add an endpoint in the dashboard (Settings → Support → Webhooks, https) and choose its events (up to five endpoints, each with its own secret); the signing secret is shown once and goes into the backend secret store as e.g. REVVLY_SUPPORT_WEBHOOK_SECRET.
- Revvly POSTs { id, type, createdAt, data: { ticketId, ticketNumber, messageId, by, endUser: { externalUserId } } } for each change a user can see, where type is one of ticket.created, ticket.message, ticket.status_changed, ticket.updated, ticket.closed, ticket.reopened, ticket.progressed (only those the endpoint chose). Ids only, except ticket.progressed, which also carries data.stage { key, label } (the step you named in Settings): read the content from the chat API with that user's token.
- Verify every request before trusting it: header Revvly-Signature: t=<unix seconds>,v1=<hex>; v1 = HMAC-SHA256(secret, "<t>.<raw request body>") compared in constant time; reject when |now − t| > 300 s. Use the raw bytes, not re-serialised JSON.
- Answer 2xx within 5 seconds and do the work afterwards. Non-2xx and timeouts are retried after 10 s, 1 min, 5 min, 30 min, 2 h and 6 h, then dropped. Delivery is at least once and may be out of order: dedupe on Revvly-Event-Id.
- Then push to that user however the product already does (its own socket, SSE or push), keyed on endUser.externalUserId.

Limits and errors on the chat endpoint
- Per user: 10 requests/second and 120/minute; for the whole workspace together 200/second and 6,000/minute; at most 10 new requests per user per hour (a retried create with the same clientMessageId does not count). Over a limit → RATE_LIMITED with a Retry-After header (seconds); for the new-request limit it says when the oldest request in the hour ages out.
- UNAUTHENTICATED: no token, invalid, expired, or its secret key was revoked → mint a new token and retry once.
- FORBIDDEN: the page's origin is not in Widget allowed origins (fix in the dashboard, not in code).
- NOT_FOUND: the request does not exist or is not the user's own.
- BAD_REQUEST: empty or too long body/title, unknown or retired type, bad cursor or page size, missing clientMessageId.
- QUERY_DEPTH_EXCEEDED: query nested deeper than 8 levels.
- INTERNAL_ERROR: safe to retry with the same clientMessageId.

Step 6 — Test before shipping
- In the Revvly dashboard, Settings → Support → Secret keys → "Test the chat as a customer" chats through this same API as a test customer, live stream included; use it to see what users will see.
- With a webhook: Settings → Support → Webhooks → "Test" posts a signed webhook.test; check the backend verifies it and answers 2xx.
- Then run the integration against staging: open a request from the product, answer it from the Revvly Support inbox (Support in the sidebar), confirm the reply appears, unreadCount clears after viewing, and closing and replying again reopens it.

Step 7 — Go-live checklist
1. The secret key is only in the backend secret store: not in the client bundle, logs, URLs or source control.
2. The token route mints only for the signed-in user from the product's own session.
3. Every production origin is in Widget allowed origins.
4. email is passed if users should be told about unread replies by email.
5. Tokens are refreshed before expiry and on UNAUTHENTICATED; nothing persists them.
6. The live stream closes with the chat, reconnects with a new ticket after an error or reauthenticate, and backs off on RATE_LIMITED; any fallback polling stops when the chat is closed or the tab is hidden.
7. Message bodies render as text.

Optional — verifying tokens on the operator's own services
- Only if their services also want to accept the token: RS256, JWKS at ${PUBLIC_API_ORIGIN}/api/v1/support/jwks.json (cacheable 1 h, header kid names the key). Require iss = "revvly-support" AND aud = the operator's Revvly tenantId (shown in the tenant context above when this prompt was copied from the dashboard; otherwise read it from the aud of any minted token and pin it). Without the aud check another workspace's token for a user with the same id would pass. A signature check does not detect a revoked key; only Revvly's endpoints do.

Clarifying questions to ask the operator first
1. Which frontend framework the product uses, and where the chat should appear (floating button, help page, in-app panel).
2. How the backend identifies the signed-in user (session, JWT) and which fields map to externalUserId, name, email, role, plan and organizationExternalId.
3. Where the support secret key will be stored, and whether it has been created yet.
4. Which origins (production, staging, local) the chat runs on, and whether they are in Widget allowed origins.
5. Whether users should get email about unread replies (then email must be passed).`,
  },
} as const satisfies Record<string, GuideDefinition>;

export type IntegrationGuideKey = keyof typeof INTEGRATION_GUIDES;

export type BuildIntegrationPromptOptions = {
  /** Marketing-site origin used to build the absolute Full guide URL. */
  docsOrigin?: string;
  /** Active Revvly tenant id from the operator's dashboard session. */
  tenantId?: string | null;
  /** Optional tenant display name for human context alongside the id. */
  tenantName?: string | null;
};

/**
 * Build a copy-paste-ready agent prompt for a guide, including the working
 * agreement, platform-wide limits, tenant context when available, and an
 * absolute docs URL.
 */
export function buildIntegrationPrompt(
  key: IntegrationGuideKey,
  options: BuildIntegrationPromptOptions | string = {},
): string {
  // Accept a bare docs-origin string for older call sites.
  const opts: BuildIntegrationPromptOptions =
    typeof options === 'string' ? { docsOrigin: options } : options;

  const guide = INTEGRATION_GUIDES[key];
  const origin = (opts.docsOrigin ?? DEFAULT_DOCS_ORIGIN).replace(/\/$/, '') || DEFAULT_DOCS_ORIGIN;
  const docsUrl = `${origin}${guide.docsPath}`;
  const tenantId = opts.tenantId?.trim();
  const tenantName = opts.tenantName?.trim();

  const tenantBlock = tenantId
    ? `Tenant context
- tenantId: ${tenantId}${tenantName ? `\n- tenantName: ${tenantName}` : ''}
- Scope all Revvly API work to this tenant. Do not switch tenants or invent another tenant id.
- If a dashboard deep link or support question comes up, refer to this tenantId.`
    : `Tenant context
- The operator has not supplied a tenantId in this prompt. Ask for the Revvly tenant id before making tenant-scoped assumptions.`;

  return `${guide.role}

${WORKING_AGREEMENT}

${tenantBlock}

${PLATFORM_LIMITS}

Full guide: ${docsUrl}

${guide.contract}`;
}

export * from './billingGuide';
